import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { useToast } from '@/components/ui/use-toast';
import { db } from '@/lib/firebase';
import { doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { useMobile } from '@/features/shared/contexts/MobileContext';

export const useOfflineCountdown = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const mobile = useMobile();
  
  const [showOfflineCountdown, setShowOfflineCountdown] = useState(false);
  const [offlineCountdown, setOfflineCountdown] = useState(5);
  const [offlineCountdownInterval, setOfflineCountdownInterval] = useState(null);
  const [isInCountdownMode, setIsInCountdownMode] = useState(false);
  const [originalSessionEndTime, setOriginalSessionEndTime] = useState(null);
  const [hasCompletedCountdown, setHasCompletedCountdown] = useState(() => {
    // Check localStorage for existing countdown completion state
    return localStorage.getItem('offlineCountdownCompleted') === 'true';
  });

  const startOfflineCountdown = async () => {
    if (!user) return;
    
    console.log('🔍 startOfflineCountdown called');
    
    // Get current session end time before starting countdown
    try {
      const liveStatusRef = doc(db, "live_status", user.uid);
      const liveStatusDoc = await getDoc(liveStatusRef);
      if (liveStatusDoc.exists()) {
        const data = liveStatusDoc.data();
        if (data.session_end_time) {
          const originalTime = data.session_end_time.toDate();
          setOriginalSessionEndTime(originalTime);
          console.log('Stored original session end time:', originalTime);
        }
      }
    } catch (error) {
      console.error("Error getting current session time:", error);
    }
    
    // Set countdown mode to prevent unwanted toasts
    setIsInCountdownMode(true);
    setHasCompletedCountdown(false);
    localStorage.removeItem('offlineCountdownCompleted');
    
    // Don't set live status to false immediately - only when countdown completes
    console.log('🔍 Setting showOfflineCountdown to true');
    setShowOfflineCountdown(true);
    setOfflineCountdown(5);
    
    const interval = setInterval(() => {
      setOfflineCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          endLiveSession();
          setShowOfflineCountdown(false);
          setIsInCountdownMode(false);
          return 5;
        }
        return prev - 1;
      });
    }, 1000);
    
    setOfflineCountdownInterval(interval);
  };

  const cancelOfflineCountdown = async () => {
    if (!user) return;
    
    console.log('Cancelling countdown, restoring session end time:', originalSessionEndTime);
    
    // Restore live status in Firestore with original session end time
    try {
      const liveStatusRef = doc(db, "live_status", user.uid);
      await updateDoc(liveStatusRef, {
        is_live: true,
        session_end_time: originalSessionEndTime,
      });
      
      console.log('Session restored in Firestore');
      
      // Wait a moment for Firestore to update
      await new Promise(resolve => setTimeout(resolve, 100));
      
      toast({ title: "Countdown Cancelled", description: "You're still live!" });
    } catch (error) {
      console.error("Error restoring live status:", error);
      toast({ title: "Error", description: "Could not restore live status.", variant: "destructive" });
    }
    
    if (offlineCountdownInterval) {
      clearInterval(offlineCountdownInterval);
      setOfflineCountdownInterval(null);
    }
    setShowOfflineCountdown(false);
    setOfflineCountdown(5);
    setIsInCountdownMode(false);
    setOriginalSessionEndTime(null);
    setHasCompletedCountdown(false);
    localStorage.removeItem('offlineCountdownCompleted');
  };

  const endLiveSession = async () => {
    if (!user) return;
    try {
      // Read current live status to capture session details
      const liveStatusRef = doc(db, "live_status", user.uid);
      let currentSessionId = null;
      let currentSessionStart = null;
      try {
        const lsDoc = await getDoc(liveStatusRef);
        if (lsDoc.exists()) {
          const d = lsDoc.data();
          currentSessionId = d.session_id || null;
          currentSessionStart = d.session_start_time ? (d.session_start_time.toDate ? d.session_start_time.toDate() : new Date(d.session_start_time)) : null;
        }
      } catch (_) {}

      // Set live status to false and clear all session data when countdown completes
      await updateDoc(liveStatusRef, {
        is_live: false,
        session_end_time: serverTimestamp(),
        // Clear session data to prevent incorrect restoration on page refresh
        session_id: null,
        session_start_time: null,
        active_playlist_id: null,
        active_song_tags: [],
      });

      // Session history is persisted once from LiveDashboardPage live_status listener (saveSessionOnEnd)
      // to avoid duplicate session_history rows and to include funnel metrics.

      // Persist session end info for Session Summary modal to consume
      try {
        // Prefer start from localStorage captured at Go Live
        const lsStart = localStorage.getItem('ysr_current_session_start');
        const startIso = lsStart || (currentSessionStart ? currentSessionStart.toISOString() : null);
        const sessionEndedData = {
          sessionId: currentSessionId || `sess-${Date.now()}`,
          startTime: startIso,
          endTime: new Date().toISOString(),
        };
        localStorage.setItem('sessionEnded', JSON.stringify(sessionEndedData));
        // Clear current session start marker
        localStorage.removeItem('ysr_current_session_start');
      } catch (_) {}
      
      // Note: Don't call goOffline() here as it causes loops
      // The Firestore update will trigger the live status listener to update the UI
      
      // Only show toast once when countdown completes (but not on mobile)
      if (!hasCompletedCountdown && !mobile.isMobile) {
        setHasCompletedCountdown(true);
        localStorage.setItem('offlineCountdownCompleted', 'true');
        toast({ title: "You are now Offline!", description: "Your session has ended." });
      }
    } catch (error) {
      console.error("Error ending session:", error);
      toast({ title: "Error", description: "Could not end session.", variant: "destructive" });
    }
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (offlineCountdownInterval) {
        clearInterval(offlineCountdownInterval);
      }
    };
  }, [offlineCountdownInterval]);

  return {
    showOfflineCountdown,
    offlineCountdown,
    isInCountdownMode,
    hasCompletedCountdown,
    startOfflineCountdown,
    cancelOfflineCountdown,
    endLiveSession,
  };
};
